import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { useState, useCallback, useEffect, useRef } from 'react'
import { LayoutDashboard, BarChart3, Plug, Database, Settings as SettingsIcon, Sparkles, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react'
import styles from './Layout.module.css'
import ChatBar from './ChatBar.jsx'
import OnboardingWizard from './OnboardingWizard.jsx'
import HelpModal from './HelpModal.jsx'
import IdleTimeoutModal from './IdleTimeoutModal.jsx'
import { PAGE_HELP } from './helpContent.js'
import { useAuth } from '../auth/AuthContext.jsx'

const NAV = [
  { to: '/dashboard',  icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/analytics',  icon: BarChart3,       label: 'Analytics' },
  { to: '/connectors', icon: Plug,            label: 'Connectors' },
  { to: '/lake',       icon: Database,        label: 'Catalog' },
  { to: '/settings',   icon: SettingsIcon,    label: 'Settings' },
]

const PAGE_PROMPTS = {
  '/connectors': [
    'When did the last QuickBooks sync complete?',
    'Are any connectors showing sync errors?',
    'How many rows did the ERP pull yesterday?',
    'Which connector has the highest data volume?',
  ],
  '/lake': [
    'Connect our Salesforce CRM to the data lake',
    'Upload a new reference CSV for plant locations',
    'Show me what dbt models are in staging',
    'Update the scrap rate model to exclude weekends',
  ],
  '/settings': [
    'What AWS region is my data lake in?',
    'How is my data encrypted at rest?',
    'Who has access to this workspace?',
  ],
}

function initials(name) {
  if (!name) return '?'
  const parts = name.split(/[@.\s]+/).filter(Boolean)
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase()
}

function UserMenu({ user, logout }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div className={styles.userMenuWrap} ref={ref}>
      <button
        className={styles.avatar}
        onClick={() => setOpen(o => !o)}
        title={user?.email ?? ''}
      >
        {initials(user?.name || user?.email)}
      </button>
      {open && (
        <div className={styles.userMenu}>
          <div className={styles.userMenuEmail}>{user?.email}</div>
          <button className={styles.userMenuSignOut} onClick={logout}>⏻ Sign out</button>
        </div>
      )}
    </div>
  )
}

function useSyncedLabel() {
  const [lastFetchTime, setLastFetchTime] = useState(Date.now())
  const [, setTick] = useState(0)

  const markFetched = useCallback(() => setLastFetchTime(Date.now()), [])

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60_000)
    return () => clearInterval(id)
  }, [])

  const elapsed = Math.floor((Date.now() - lastFetchTime) / 60_000)
  const label = elapsed < 1 ? 'Just now'
    : elapsed === 1 ? '1 min ago'
    : elapsed < 60 ? `${elapsed} min ago`
    : `${Math.floor(elapsed / 60)} hr ago`

  return { label, markFetched }
}

export default function Layout() {
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [pendingPrompt, setPendingPrompt] = useState('')
  const [helpPrompt, setHelpPrompt] = useState('')
  const [tenantProfile, setTenantProfile] = useState(null)
  const [profileLoaded, setProfileLoaded] = useState(false)
  const [dataRefreshKey, setDataRefreshKey] = useState(0)
  const [tenants, setTenants] = useState([])
  const { label: syncedLabel, markFetched } = useSyncedLabel()
  const triggerDataRefresh = useCallback(() => { setDataRefreshKey(k => k + 1); markFetched() }, [markFetched])
  const { user, logout, apiFetch, selectedTenantId, switchTenant } = useAuth()
  const isPlatformAdmin = user?.groups?.includes('platform-admin') ?? false
  const activeTenant = tenants.find(t => t.tenant_id === selectedTenantId)

  useEffect(() => {
    if (!user) return
    apiFetch('/api/tenant/profile')
      .then(r => r.json())
      .then(p => { setTenantProfile(p); setProfileLoaded(true) })
      .catch(() => setProfileLoaded(true))
  }, [user, apiFetch])

  useEffect(() => {
    if (!isPlatformAdmin || !user) return
    apiFetch('/api/admin/customers')
      .then(r => r.json())
      .then(d => setTenants(d.tenants ?? []))
      .catch(() => {})
  }, [isPlatformAdmin, user]) // intentionally omit apiFetch — tenant list uses own-account context

  const openChat = useCallback((prompt = '') => {
    setChatOpen(true)
    if (prompt) setPendingPrompt(prompt)
  }, [])

  const consumeHelpPrompt = useCallback(() => setHelpPrompt(''), [])

  const isAnalytics = location.pathname === '/analytics'
  const isFlush = isAnalytics || location.pathname === '/dashboard'
  const current = NAV.find(n => location.pathname.startsWith(n.to))
  const prompts = PAGE_PROMPTS[current?.to] ?? []
  const pageContext = current?.to?.replace('/', '') ?? ''

  const showWizard = profileLoaded && !tenantProfile?.onboarded_at

  return (
    <div className={`${styles.root} ${collapsed ? styles.collapsed : ''}`}>
      {showWizard && (
        <OnboardingWizard onComplete={saved => setTenantProfile(saved)} />
      )}
      <IdleTimeoutModal />
      <HelpModal
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
        icon={current?.icon}
        content={current?.to ? PAGE_HELP[current.to] : null}
        onAskAI={prompt => { setHelpOpen(false); openChat(prompt); setHelpPrompt(prompt) }}
      />
      {/* Sidebar */}
      <aside className={styles.sidebar}>
        <div className={styles.brand}>
          <svg viewBox="0 0 32 32" width="28" height="28" className={styles.brandIcon}>
            <rect x="2"  y="4"  width="28" height="7" rx="2" fill="none" stroke="#38BDF8" strokeWidth="1.5" opacity="0.5"/>
            <rect x="2"  y="13" width="28" height="7" rx="2" fill="none" stroke="#38BDF8" strokeWidth="1.5"/>
            <rect x="2"  y="22" width="28" height="7" rx="2" fill="none" stroke="#38BDF8" strokeWidth="1.5" opacity="0.5"/>
            <line x1="9"  y1="14.5" x2="9"  y2="18.5" stroke="#38BDF8" strokeWidth="1.2" opacity="0.7"/>
            <line x1="16" y1="14.5" x2="16" y2="18.5" stroke="#38BDF8" strokeWidth="1.2" opacity="0.7"/>
            <line x1="23" y1="14.5" x2="23" y2="18.5" stroke="#38BDF8" strokeWidth="1.2" opacity="0.7"/>
          </svg>
          {!collapsed && <span className={styles.brandName}>Structura</span>}
        </div>

        <nav className={styles.nav}>
          {NAV.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.navActive : ''}`
              }
            >
              <span className={styles.navIcon}><Icon size={16} strokeWidth={2} /></span>
              {!collapsed && <span className={styles.navLabel}>{label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <div className={styles.orgBadge}>
            {!collapsed && (
              <>
                <span className={`${styles.orgDot} ${selectedTenantId ? styles.orgDotAdmin : ''}`} />
                <span className={styles.orgName}>
                  {selectedTenantId
                    ? (activeTenant?.company_name ?? selectedTenantId)
                    : (tenantProfile?.company_name ?? 'Your Organization')}
                </span>
              </>
            )}
          </div>
          <button
            className={styles.collapseBtn}
            onClick={() => setCollapsed(c => !c)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className={styles.main}>
        <header className={styles.topbar}>
          <div className={styles.topbarLeft}>
            <span className={styles.breadcrumb}>
              {current?.icon && (
                <span className={styles.breadcrumbIcon}><current.icon size={15} strokeWidth={2} /></span>
              )}
              {current?.label}
            </span>
          </div>
          <div className={styles.topbarRight}>
            <button
              className={styles.helpBtn}
              onClick={() => setHelpOpen(true)}
              title={`How to use ${current?.label ?? 'this page'}`}
            >
              <HelpCircle size={18} strokeWidth={2} />
            </button>
            {isPlatformAdmin && tenants.length > 0 && (
              <select
                className={`${styles.tenantSwitcher} ${selectedTenantId ? styles.tenantSwitcherActive : ''}`}
                value={selectedTenantId || ''}
                onChange={e => switchTenant(e.target.value || null)}
              >
                <option value=''>My account</option>
                {tenants.map(t => (
                  <option key={t.tenant_id} value={t.tenant_id}>
                    {t.company_name || t.tenant_id}
                  </option>
                ))}
              </select>
            )}
            <div className={styles.syncStatus}>
              <span className={styles.syncDot} />
              <span className={styles.syncLabel}>Synced {syncedLabel}</span>
            </div>
            <UserMenu user={user} logout={logout} />
          </div>
        </header>

        {selectedTenantId && (
          <div className={styles.adminBanner}>
            <span>Admin view &mdash; {activeTenant?.company_name ?? selectedTenantId}</span>
            <button className={styles.adminBannerExit} onClick={() => switchTenant(null)}>
              Exit
            </button>
          </div>
        )}

        <div className={styles.body}>
          <div className={`${styles.content} ${isFlush ? styles.contentFlush : ''}`}>
            <Outlet context={{ openChat, tenantProfile, setTenantProfile, dataRefreshKey, helpPrompt, consumeHelpPrompt }} />
          </div>

          {!isFlush && (
            <ChatBar
              key={`${user?.sub}-${selectedTenantId || 'own'}`}
              userId={user?.sub}
              isOpen={chatOpen}
              onClose={() => setChatOpen(false)}
              suggestedPrompts={prompts}
              pageContext={pageContext}
              pendingPrompt={pendingPrompt}
              onPendingPromptConsumed={() => setPendingPrompt('')}
              onDataChanged={triggerDataRefresh}
            />
          )}
        </div>

        {!isFlush && (
          <button
            className={`${styles.fab} ${chatOpen ? styles.fabActive : ''}`}
            style={{ right: chatOpen ? `${380 + 20}px` : '20px' }}
            onClick={() => setChatOpen(o => !o)}
            title={chatOpen ? 'Close AI chat' : 'Ask AI'}
          >
            <Sparkles size={20} strokeWidth={2} />
          </button>
        )}
      </main>
    </div>
  )
}
